import { useState } from "react";
import { reasonLabel } from "../data/reasons";
import type { CodeCaseQuery, OrgDashboard, ReasonCount } from "../types";
import { fieldLabel } from "./codeTableToolbar";

/** A ranked reason list where each reason expands to the codes it was cited
 *  against. `field` fixes the field for the drill-in query (per-field lists);
 *  the study-wide list takes the field from each code row. `canDrill` is the
 *  server's answer to "is this an auditor" (it sent example ids), so a
 *  reviewer sees codes as text rather than as links to a list they would be
 *  refused. */
export function ReasonList({
  reasons,
  field,
  canDrill,
  onOpenCodeCases,
}: {
  reasons: ReasonCount[];
  field?: string;
  canDrill: boolean;
  onOpenCodeCases?: (q: CodeCaseQuery) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const top = reasons[0]?.count ?? 0;
  return (
    <ul className="flex flex-col gap-1.5">
      {reasons.map((r) => {
        const isOpen = open === r.reason;
        return (
          <li key={r.reason} className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : r.reason)}
              aria-expanded={isOpen}
              className="flex items-baseline justify-between gap-4 text-left text-[13px] text-foreground hover:text-coronis-blue-600"
            >
              <span>
                {reasonLabel(r.reason)} <span className="text-[11px] text-muted-foreground">{isOpen ? "▾" : "▸"}</span>
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">{r.count.toLocaleString()}</span>
            </button>
            <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-coronis-teal-500" style={{ width: `${top > 0 ? (r.count / top) * 100 : 0}%` }} />
            </div>
            {isOpen && (
              <div className="flex flex-wrap items-center gap-1.5 pb-1 pl-2">
                {r.codes.length === 0 ? (
                  // The floor: a reason with no code above it names no case.
                  <span className="text-[11.5px] text-muted-foreground">No code cited 3 or more times for this reason.</span>
                ) : (
                  r.codes.map((c) => {
                    const codeField = field ?? c.field;
                    return canDrill && onOpenCodeCases ? (
                      <button
                        key={`${c.field}:${c.code}`}
                        type="button"
                        onClick={() => onOpenCodeCases({ mode: "errored", reason: r.reason, field: codeField, code: c.code })}
                        className="rounded-coronis border border-border bg-card px-2 py-0.5 text-[12px] tabular-nums text-foreground hover:bg-muted"
                      >
                        <span className="font-medium">{c.code}</span> · {fieldLabel(codeField)} · {c.count}
                      </button>
                    ) : (
                      <span
                        key={`${c.field}:${c.code}`}
                        className="rounded-coronis border border-border bg-card px-2 py-0.5 text-[12px] tabular-nums text-foreground"
                      >
                        <span className="font-medium">{c.code}</span> · {fieldLabel(codeField)} · {c.count}
                      </span>
                    );
                  })
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Why each field goes wrong, ranked per field. Moved out of the dashboard
 *  unchanged in meaning; each reason now expands to its codes. */
export function FieldReasonsSection({
  fields,
  canDrill,
  onOpenCodeCases,
}: {
  fields: OrgDashboard["fields"];
  canDrill: boolean;
  onOpenCodeCases?: (q: CodeCaseQuery) => void;
}) {
  const withReasons = fields.filter((f) => f.topReasonCounts.length > 0);
  if (withReasons.length === 0) return null;
  return (
    <div className="flex flex-col gap-2.5">
      <h2 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Why each field goes wrong
      </h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {withReasons.map((f) => (
          <div key={f.field} className="flex flex-col gap-2 rounded-coronis border border-border bg-card px-4 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[13px] font-semibold text-foreground">{fieldLabel(f.field)}</span>
              <span className="shrink-0 text-[11.5px] tabular-nums text-muted-foreground">{f.judged.toLocaleString()} judged</span>
            </div>
            <ReasonList reasons={f.topReasonCounts} field={f.field} canDrill={canDrill} onOpenCodeCases={onOpenCodeCases} />
          </div>
        ))}
      </div>
    </div>
  );
}
